import React from "react";
import {
  FieldPath,
  FieldValues,
  PathValue,
  useController,
  UseControllerProps,
  Validate,
} from "react-hook-form";
import {
  IconButton,
  Icons,
  Text,
  TimeInput,
} from "@pushpress/shared-components";
import { WorkingHour } from "./WorkingHoursDrawer.types";
import styles from "./WorkingHourController.module.scss";

export interface WorkingHourControllerProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends UseControllerProps<TFieldValues, TName> {
  hideWeekDay?: boolean;
  hideAddButton?: boolean;
  hideDeleteButton?: boolean;
  validate?: Validate<PathValue<TFieldValues, TName>>;
  onDelete: VoidFunction;
  onAdd: VoidFunction;
}

function WorkingHourController<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(props: WorkingHourControllerProps<TFieldValues, TName>) {
  const {
    name,
    control,
    hideWeekDay,
    hideAddButton,
    hideDeleteButton,
    validate,
    onDelete,
    onAdd,
  } = props;

  const {
    field: { value, onChange, onBlur },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: {
      validate,
    },
  });

  const handleStartChange = (time: string) => {
    onChange({
      ...value,
      startTime: time,
    });
  };

  const handleEndChange = (time: string) => {
    onChange({
      ...value,
      endTime: time,
    });
  };

  const { dayOfTheWeek, startTime, endTime, enabled } = value as WorkingHour;

  const hasError = !!error;

  return (
    <div className={styles.root}>
      <div className={styles.inputs}>
        <div className={styles.week}>
          {!hideWeekDay && (
            <Text variant="body-lg" color="wolf-800">
              {dayOfTheWeek.slice(0, 3)}
            </Text>
          )}
        </div>
        <div className={styles.times}>
          {enabled ? (
            <>
              <div className={styles.start}>
                <TimeInput
                  error={hasError}
                  value={startTime}
                  minuteStep={5}
                  aria-label="Start"
                  onValueChange={handleStartChange}
                  onBlur={onBlur}
                />
              </div>
              <div className={styles.connector}>
                <Icons.ArrowRight width={16} height={16} color="#A3B1BD" />
              </div>
              <div className={styles.end}>
                <TimeInput
                  error={hasError}
                  value={endTime}
                  minuteStep={5}
                  aria-label="End"
                  onValueChange={handleEndChange}
                  onBlur={onBlur}
                />
              </div>
            </>
          ) : (
            <Text variant="body-lg" color="wolf-600">
              Unavailable
            </Text>
          )}
        </div>
        <div className={styles.action}>
          {!hideDeleteButton && (
            <IconButton
              icon={Icons.Trash}
              size={18}
              onClick={onDelete}
              aria-label="Delete"
              color="#2A3540"
            />
          )}
        </div>
        <div className={styles.action}>
          {!hideAddButton && (
            <IconButton
              icon={Icons.AddCircle}
              size={20}
              onClick={onAdd}
              aria-label="Add"
              color="#2A3540"
            />
          )}
        </div>
      </div>
      <div className={styles.message}>
        <Text
          variant="body-md"
          color="wild-watermellon-600"
          className={styles.error}
        >
          {error?.message}
        </Text>
      </div>
    </div>
  );
}

export default WorkingHourController;
